<template>
  <article class="order-complete">
    <i18n-t v-if="hasPayment" keypath="general.order_complete_description1_2" tag="p" class="order-complete__header">
      <template #payment>
        <span>{{ t('general.payment') }}</span>
      </template>
      <template #complete>
        <span>{{ t('general.complete') }}</span>
      </template>
    </i18n-t>
    <i18n-t v-else keypath="general.order_complete_description1" tag="p" class="order-complete__header">
      <template #order>
        <span>{{ t('general.order') }}</span>
      </template>
      <template #complete>
        <span>{{ t('general.complete') }}</span>
      </template>
    </i18n-t>
    <div class="order-complete__body">
      <p v-if="configStore.config.CultureName !== 'ja-JP'" class="body__contents1">
        {{ t('general.order_complete_description2') }}
      </p>
      <p class="body__contents2">{{ t('general.order_complete_description3') }}</p>
      <div v-if="adList.length > 0" class="body__ad">
        <video
          ref="videoElement"
          :key="currentVideo.videoUrl"
          class="video"
          :src="currentVideo.videoUrl"
          :poster="currentVideo.thumbnailUrl"
          preload="auto"
          autoplay
          muted
          @play="handleVideoPlay"
          @playing="handleVideoPlaying"
          @ended="handleVideoEnded"
          @timeupdate="handleVideoTimeUpdate"
        ></video>
      </div>
      <p v-else class="body__image"><img src="@/assets/images/img-order-complete.svg" alt="주문 완료 이미지" /></p>
    </div>
    <div class="order-complete__footer">
      <div class="footer__button">
        <BaseButton :count="autoCloseTime" @button-click="handleConfirmClick">
          <template #icon>
            <img src="@/assets/images/icon-check-white.svg" alt="확인 아이콘" />
          </template>
          {{ t('button.confirm') }}
        </BaseButton>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
  import * as Android from '@/api/android';
  import AppTreeAdLogRequest from '@/api/android/interfaces/AppTreeAdLogRequest';
  import AppTreeAdResponse from '@/api/android/interfaces/AppTreeAdResponse';
  import BaseButton from '@/components/base/BaseButton.vue';
  import { MinOrderQtyType } from '@/enums/MinOrderQtyType';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { useOrderStore } from '@/stores/order';
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  defineProps<{ hasPayment: boolean }>();
  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const commonStore = useCommonStore();
  const configStore = useConfigStore();
  const menuStore = useMenuStore();
  const orderStore = useOrderStore();

  let timer: any;
  let adLogTimer: any;
  // 자동 모달 닫는 시간 (초)
  const autoCloseTime = ref(0);
  // 광고 리스트
  const adList = ref<AppTreeAdResponse[]>([]);
  // 비디오 엘리먼트
  const videoElement = ref<HTMLVideoElement>();
  // 현재 재생 중인 광고 비디오 인덱스
  const currentVideoIndex = ref(0);
  // 마지막으로 전송한 광고 로그 진행률
  const adLogDepth = ref(0);

  // 현재 재생중인 비디오
  const currentVideo = computed(() => adList.value[currentVideoIndex.value]);
  // 총 재생시간
  const totalVideoDuration = computed(() =>
    adList.value.reduce((accomulator, currentItem) => accomulator + currentItem.duration, 0),
  );

  onMounted(async () => {
    const loadingId = commonStore.showLoading();
    try {
      if (configStore.config.IsADUsed) {
        // Android에서 광고 조회
        adList.value = await Android.getAppTreeAdAsync();
      }

      // 조회된 광고가 있는 경우
      if (adList.value.length > 0) {
        // 총 재생시간으로 자동 닫기 시간 업데이트
        autoCloseTime.value = totalVideoDuration.value;
      } else {
        // 기본값으로 자동 닫기 시간 업데이트
        autoCloseTime.value = 5;
      }

      timer = setInterval(() => {
        if (--autoCloseTime.value === 0) {
          handleConfirmClick();
        }
      }, 1000);

      // POS에서 한정수량 데이터 조회
      if (configStore.config.IsLimitedItemUsed) {
        const response = await Android.getOrderLimitItemsForOrderAsync();

        if (response) {
          if (response.RET_CODE !== 0) return;
          menuStore.setLimitMenus(response.DATA);
        }
      }

      // 최소주문수량 또는 후식메뉴 옵션을 사용하는 경우 주문 조회하여 저장
      if (configStore.config.MinOrderQtyType !== MinOrderQtyType.None || configStore.config.DessertType !== '0') {
        // 이미 주문된 수량 확인을 위해 주문 조회
        const response = await Android.getOrderDataForOrderAsync();

        // 주문 조회 성공 여부 검사 후 저장
        if (response.RET_CODE === 0) orderStore.setOrder(response.DATA);
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  });

  onBeforeUnmount(() => {
    clearInterval(timer);
    clearInterval(adLogTimer);
    // 광고 중지 로그 전송
    sendAdLog('4');
  });

  /**
   * 광고 전송
   * @param type 로그 타입
   * 1 : 노출로그 (광고 시작 시 호출)
   * 2 : 조회 로그 (광고 시작 후 5초 후에 호출)
   * 3 : 플레이 로그 (동영상 플레이 시작 시 호출)
   * 4 : 스킵 로그 (중지 시 호출)
   * 5 : 구간 로그 (동영상 플레이 중 호출)
   * @param depth 구간 로그 호출 시 동영상 재생률
   * 1 : 25%
   * 2 : 50%
   * 3 : 70%
   * 4 : 100%
   */
  const sendAdLog = (type: '1' | '2' | '3' | '4' | '5', depth?: '1' | '2' | '3' | '4') => {
    if (!currentVideo.value) return;
    const request: AppTreeAdLogRequest = {
      type: type,
      unitId: currentVideo.value.unitId,
      adsId: currentVideo.value.adsId,
      crId: currentVideo.value.crId,
      rnd: currentVideo.value.rnd,
      depth,
    };
    Android.appTreeAdLog(request);
  };

  const handleVideoPlay = () => {
    // 광고 동영상 노출 로그 전송
    sendAdLog('1');

    // 광고 조회 로그 전송 (5초 후)
    clearInterval(adLogTimer);
    let time = 5;
    adLogTimer = setInterval(() => {
      if (--time === 0) {
        sendAdLog('2');
      }
    }, 1000);
  };

  /** 비디오 시작 이벤트 */
  const handleVideoPlaying = () => {
    // 광고 동영상 시작 로그 전송
    sendAdLog('3');
  };

  /** 비디오 시간 업데이트 이벤트 */
  const handleVideoTimeUpdate = () => {
    if (!videoElement.value) return;
    const videoTime = videoElement.value.duration;
    const currentTime = videoElement.value.currentTime;
    const percentage = Math.ceil((currentTime / videoTime) * 100);

    if (adLogDepth.value < 1 && percentage >= 25 && percentage < 50) {
      adLogDepth.value = 1;
      sendAdLog('5', '1');
    } else if (adLogDepth.value < 2 && percentage >= 50 && percentage < 75) {
      adLogDepth.value = 2;
      sendAdLog('5', '2');
    } else if (adLogDepth.value < 3 && percentage >= 75 && percentage < 100) {
      adLogDepth.value = 3;
      sendAdLog('5', '3');
    } else if (adLogDepth.value < 4 && percentage === 100) {
      adLogDepth.value = 4;
      sendAdLog('5', '4');
    }
  };

  /** 비디오 종료 이벤트 */
  const handleVideoEnded = () => {
    if (adList.value.length > currentVideoIndex.value + 1) {
      currentVideoIndex.value++;
      adLogDepth.value = 0;
    }
  };

  /** 확인 버튼 클릭 */
  const handleConfirmClick = () => {
    const { IsAdvertisementUsed, IsWidgetModeUsed, IsWaitingAfterOrder } = configStore.config;
    emits('close');

    if (IsWidgetModeUsed) {
      Android.showBanner();
    } else if (IsAdvertisementUsed && IsWaitingAfterOrder) {
      Android.showBanner();
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .order-complete {
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    align-items: center;
    height: 100vh;
    padding: 2.9851vw;
    @media (max-width: vars.$md) {
      padding: 20px;
    }
    .order-complete__header {
      font-size: 3.7313vw;
      font-weight: bold;
      @media (max-width: vars.$md) {
        font-size: 32px;
      }
      > span {
        color: #d7201a;
      }
    }

    .order-complete__body {
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
      align-items: center;
      gap: 1.4925vw;
      font-size: 2.0896vw;
      text-align: center;
      @media (max-width: vars.$md) {
        font-size: 18px;
      }
      .body__contents2 {
        color: #7e7e7e;
      }
      .video {
        height: 33.5821vw;
      }
    }

    .order-complete__footer {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      .footer__button {
        width: 31.3433vw;
        @media (max-width: vars.$md) {
          width: 50%;
        }
        @media (max-width: vars.$sm) {
          width: 100%;
        }
      }
    }
  }
</style>
